/**
 * 반복 행 「행 추가 허용 / 행 삭제 허용」 하위 토글 한 벌 — 데이터소스 탭(선언 `DataSourceDef`)과
 * 양식 표(formTable 반복 행 오버라이드) 두 패널이 같은 컴포넌트를 쓴다 — 패널마다 복제해
 * `masterOn`을 각자 재계산하지 말 것.
 *
 * 표시값·비활성 판정은 rc가 쓰는 같은 순수 함수(shared `effectiveRowAddRemove` — 상위 스위치 false면
 * 둘 다 false)라 체크 상태 = 지금 작성폼의 실제 동작. 상위 스위치는 shared `rowAddRemoveMaster`
 * (같은 층 순서: 노드 → 선언 → true) — 꺼지면 둘 다 해제·비활성 + 사유 한 줄(서버 조용한 게이트 ↔
 * admin 미러 규약 동형). 사유 문구는 패널마다 달라(데이터소스 탭은 「상위를 켜면 저장된 하위 체크가
 * 그대로 적용됩니다」를 덧붙인다) 호출부가 글자 그대로 넘긴다 — 은닉 감사 통과 문구를 여기서 바꾸지
 * 않는다.
 */
import {
  effectiveRowAddRemove,
  rowAddRemoveMaster,
  type RowAddRemoveSwitches,
} from '@safetyhub/shared';

/** 패널별 결(간격·글자 크기·체크박스 크기) — 두 호출부의 종전 클래스 그대로. */
const VARIANT_CLS = {
  /** 데이터소스 탭: 편집 모드 행 아래 들여쓰기(pl-[4.5rem]) + 11px. */
  dataSource: {
    wrapper: 'pl-[4.5rem] flex flex-col gap-0.5 text-[11px] text-gray-500',
    label: 'flex items-center gap-1',
    checkbox: undefined,
  },
  /** 양식 표 반복 행 패널: 들여쓰기 없음 + text-xs + h-4 체크박스. */
  formTableRow: {
    wrapper: 'flex flex-col gap-0.5 text-xs text-gray-600',
    label: 'flex items-center gap-2',
    checkbox: 'h-4 w-4',
  },
} as const;

export interface RowAddRemoveTogglesProps {
  /** 선언(`DataSourceDef`) — 미지정이면 모든 키가 기본(true). */
  def?: RowAddRemoveSwitches | null;
  /** 노드 오버라이드(formTable 반복 행) — 키마다 def보다 우선. */
  node?: RowAddRemoveSwitches | null;
  onAddChange: (checked: boolean) => void;
  onRemoveChange: (checked: boolean) => void;
  variant: keyof typeof VARIANT_CLS;
  /** 상위 스위치가 꺼졌을 때의 사유 문구(호출부 글자 그대로). */
  masterOffNote: string;
}

export function RowAddRemoveToggles({
  def,
  node,
  onAddChange,
  onRemoveChange,
  variant,
  masterOffNote,
}: RowAddRemoveTogglesProps) {
  const cls = VARIANT_CLS[variant];
  const masterOn = rowAddRemoveMaster(def, node);
  const eff = effectiveRowAddRemove(def, node);
  const labelCls = `${cls.label} ${masterOn ? '' : 'opacity-50'}`;
  return (
    <div className={cls.wrapper}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
        <label className={labelCls}>
          <input
            type="checkbox"
            className={cls.checkbox}
            disabled={!masterOn}
            checked={eff.add}
            onChange={(e) => onAddChange(e.target.checked)}
          />
          행 추가 허용
        </label>
        <label className={labelCls}>
          <input
            type="checkbox"
            className={cls.checkbox}
            disabled={!masterOn}
            checked={eff.remove}
            onChange={(e) => onRemoveChange(e.target.checked)}
          />
          행 삭제 허용
        </label>
      </div>
      {!masterOn && <p className="text-[10px] leading-4 text-gray-400">{masterOffNote}</p>}
    </div>
  );
}

export default RowAddRemoveToggles;
